import {Component, OnInit, ViewEncapsulation, ViewChild, DestroyRef, inject} from '@angular/core';
import {mmsAdminOverrideFilterFieldMap as fieldMap} from '@emm/shared/formly/maps/mms-admin-filter.config';
import {BehaviorSubject, of} from 'rxjs';
import {map, tap} from 'rxjs/operators';
import {FormArray, FormBuilder, FormGroup, Validators} from '@angular/forms';
import * as R from 'ramda';
import {MasterMenuSelectionModel} from '@emm/core/models/master-menu-selection.model';
import {MatStepper} from '@angular/material/stepper';
import {GenericItemsTreeComponent} from '../generic-items-tree/generic-items-tree.component';
import dayjs from 'dayjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {MmsAdminOverrideFacadeService} from '@emm/management/services/mms-admin-override-facade.service';
import {ManagementApiService} from '@emm/management/services/management-api.service';
import {NotificationService} from '@emm/shared/services/notification.service';
import {UserService} from '@emm/core/services/user.service';

@Component({
    encapsulation: ViewEncapsulation.None,
    selector: 'emm-mms-admin',
    styleUrls: ['./mms-admin.component.scss'],
    templateUrl: './mms-admin.component.html',
    standalone: false
})
export class MmsAdminComponent implements OnInit {
    @ViewChild('optionStepper') private optionStepper: MatStepper;
    @ViewChild(GenericItemsTreeComponent) tree: GenericItemsTreeComponent;
    fields$ = of(null).pipe(map((_) => [fieldMap['name']()]));
    centerLoading$ = this.mmsAdminFacade.centerLoading$;
    filteredOptions$ = this.mmsAdminFacade.filteredOptions$;
    effectiveDate: any;
    readOnlyEffectiveDate: any;
    storeNumber: any;
    selected: any;
    menuOptions;
    selectionsFormGroup: FormGroup;
    nodes = [];
    nodes$ = new BehaviorSubject<any[]>(undefined);
    isReadOnly = this.userService.hasLoginRole();
    isSuperAdmin = this.userService.hasSuperAdminRole();
    isAdmin = this.userService.hasAdminRole();
    isAuthor = this.userService.hasAuthorRole();

    templateByLocation$ = this.mmsAdminFacade.templateByLocation$.pipe(tap((data) => {
        if (R.isNotEmpty(data)) {
            this.menuOptions = data;
            data.items.forEach((item, i) => {
                this.categories.push(this.createCategorySelection(item.tag, item.name, item.items, i));
            });
        }
    }));

    templateOptionByLocation$ = this.mmsAdminFacade.templateOptionByLocationAsNode$.pipe(
        tap((items) => {
            if (R.isNotEmpty(items) && items[0] === 409) {
                this.nodes = null;
                this.nodes$.next(this.nodes);
            } else if (R.isNotEmpty(items)) {
                this.readOnlyEffectiveDate = dayjs(items.selection.effectiveDate).format('MM-DD-YYYY');
                this.nodes = items.items.map(this.mapTemplateParentGroupingsToNode);
                this.nodes$.next(this.nodes);
            }
        })
    );
    options = {};
    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    constructor(
        private mmsAdminFacade: MmsAdminOverrideFacadeService,
        private formBuilder: FormBuilder,
        private api: ManagementApiService,
        private notificationService: NotificationService,
        private userService: UserService,) {
    }

    ngOnInit(): void {
        this.mmsAdminFacade.init();
        this.selectionsFormGroup = this.formBuilder.group({
            categories: this.formBuilder.array([])
        });
        this.templateByLocation$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
        this.templateOptionByLocation$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    }

    onFilter(filtered) {
        this.mmsAdminFacade.searchLocation(filtered);
    }

    onSelectingLocation(selected) {
        this.api.getMenuSource(selected.tag)
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((result) => {
                if (result.status === 200) {
                    this.nodes = [];
                    this.categories.clear();
                    this.mmsAdminFacade.selectedLocation(selected);
                    this.storeNumber = selected.tag;
                    this.selected = selected;
                }
            },
            error => {
                if (error.status === 409) {
                    this.notificationService.error(`${selected.name} is not supported by MMS`);
                }
            });
    }

    selectEffectiveDate(selected) {
        this.effectiveDate = selected;
    }

    createCategorySelection(tag: string, title: string, items, id: number): FormGroup {
        const categorySelection = this.formBuilder.group({
            tag: [tag],
            title: [title],
            id: [id],
            selectedValue: [undefined, Validators.required],
            options: this.formBuilder.array([])
        });
        const opts = categorySelection.get('options') as FormArray;
        items.forEach((item, i) => {
            opts.push(this.formBuilder.group({
                id: [i],
                tag: [item.tag],
                name: [item.name],
                items: [item.items],
            }));
            if (item.selected) {
                categorySelection.patchValue({selectedValue: item});
            }
        });
        return categorySelection;
    }

    canSave(): boolean {
        return this.selectionsFormGroup && this.selectionsFormGroup.valid && this.selectionsFormGroup.dirty;
    }

    get categories(): FormArray {
        return this.selectionsFormGroup.get('categories') as FormArray;
    }

    onSave() {
        const selectedEffectiveDate = this.effectiveDate ? this.effectiveDate : null;
        if (selectedEffectiveDate) {
            const selectedCategories = this.selectionsFormGroup.get('categories').value;
            const request: MasterMenuSelectionModel = {
                location: this.storeNumber,
                selections: []
            };
            selectedCategories.forEach((category) => {
                request.selections.push({
                    submenu: category?.tag,
                    selectedOption: category?.selectedValue?.tag
                });
            });
            this.mmsAdminFacade.saveTemplateSelections(request, this.storeNumber, selectedEffectiveDate);
        } else {
            this.notificationService.error('Please select an effective date.');
            return;
        }
    }

    private mapTemplateParentGroupingsToNode(template: any): any {
        let nameWithOption = '';
        const getChildren = [];
        template.items.forEach((item) => {
            if (item.selected) {
                nameWithOption = `${template.name} - ${item.name}`;
                getChildren.push(item);
            }
        });
        const childObj = [];
        if (getChildren.length && getChildren[0].items) {
            getChildren[0].items.forEach((childNode) => {
                childObj.push({
                    id: childNode.tag,
                    name: childNode.name
                });
            });
        }
        return {
            id: template.tag,
            name: nameWithOption,
            children: childObj,
        };
    }
}
